import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Alert, Button, Container, Input, Label, Spinner } from "reactstrap";

import AdatisLogo from "../../../Components/Common/AdatisLogo";
import {
  getPublicAppointmentReview,
  PublicAppointmentReviewPreview,
  submitPublicAppointmentReview,
} from "../../../services/publicAppointmentReviewService";

const PublicAppointmentReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const { token = "" } = useParams<{ token: string }>();

  const [preview, setPreview] = useState<PublicAppointmentReviewPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submittedRating, setSubmittedRating] = useState<number | null>(null);
  const [googleReviewUrl, setGoogleReviewUrl] = useState<string | null>(null);

  document.title = t("public_review.pageTitle");

  const load = useCallback(async (): Promise<void> => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setNotFound(false);
    setExpired(false);

    try {
      const response = await getPublicAppointmentReview(token);
      const data = response.data ?? null;
      setPreview(data);

      if (data?.already_submitted) {
        setSubmittedRating(null);
        setGoogleReviewUrl(data.google_review_url);
      }
    } catch (err) {
      const apiError = err as { code?: string; status?: number };

      if (apiError.code === "APPOINTMENT_REVIEW_TOKEN_EXPIRED") {
        setExpired(true);
        return;
      }

      if (
        apiError.code === "APPOINTMENT_REVIEW_TOKEN_INVALID" ||
        apiError.code === "NOT_FOUND"
      ) {
        setNotFound(true);
        return;
      }

      setError(t("public_review.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (): Promise<void> => {
    if (!token || rating < 1 || rating > 5) {
      setError(t("public_review.errors.ratingRequired"));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await submitPublicAppointmentReview(token, {
        rating,
        comment: comment.trim() || undefined,
      });
      const data = response.data;
      setSubmittedRating(data?.rating ?? rating);
      setGoogleReviewUrl(data?.google_review_url ?? null);
      setPreview((prev) => (prev ? { ...prev, already_submitted: true } : prev));
    } catch (err) {
      const apiError = err as { code?: string; message?: string };

      if (apiError.code === "APPOINTMENT_REVIEW_TOKEN_EXPIRED") {
        setExpired(true);
        return;
      }

      if (apiError.code === "APPOINTMENT_REVIEW_TOKEN_INVALID") {
        setNotFound(true);
        return;
      }

      setError(apiError.message || t("public_review.errors.submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const displayRating = hoverRating || rating;
  const showThankYou =
    submittedRating !== null || (preview?.already_submitted === true && !loading);

  return (
    <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
      <Container style={{ maxWidth: 520 }}>
        <div className="text-center mb-4">
          <AdatisLogo variant="light" height={28} linkTo={false} />
          <p className="text-white-50 mt-2 mb-0">{t("public_review.subtitle")}</p>
        </div>

        {loading && (
          <div className="text-center py-5">
            <Spinner color="light" />
            <p className="text-white-50 mt-3 mb-0">{t("public_review.loading")}</p>
          </div>
        )}

        {!loading && (expired || notFound || (error && !preview)) && (
          <div className="bg-white rounded-3 p-4 shadow-sm">
            {expired && (
              <Alert color="warning" className="mb-0">
                {t("public_review.errors.expired")}
              </Alert>
            )}
            {notFound && (
              <Alert color="danger" className="mb-0">
                {t("public_review.errors.notFound")}
              </Alert>
            )}
            {error && !preview && !expired && !notFound && (
              <Alert color="danger" className="mb-0">
                {error}
              </Alert>
            )}
          </div>
        )}

        {!loading && preview && (
          <div className="bg-white rounded-3 p-4 shadow-sm">
            <h4 className="mb-1">
              {preview.tenant?.name || t("public_review.fallbackStudio")}
            </h4>
            <p className="text-muted mb-3">{t("public_review.heading")}</p>

            {preview.scheduled_label && (
              <p className="mb-1">
                <strong>{t("public_review.fields.when")}:</strong> {preview.scheduled_label}
              </p>
            )}
            {preview.service?.name && (
              <p className="mb-1">
                <strong>{t("public_review.fields.service")}:</strong> {preview.service.name}
              </p>
            )}
            {preview.professional?.name && (
              <p className="mb-3">
                <strong>{t("public_review.fields.professional")}:</strong>{" "}
                {preview.professional.name}
              </p>
            )}

            {showThankYou ? (
              <>
                <Alert color="success" className="mb-3">
                  {t("public_review.feedback.thanks")}
                </Alert>
                {submittedRating !== null && (
                  <p className="fs-3 mb-3 text-warning" aria-label={`${submittedRating} stars`}>
                    {"★".repeat(submittedRating)}
                    <span className="text-muted">{"★".repeat(5 - submittedRating)}</span>
                  </p>
                )}
                {(googleReviewUrl || preview.google_review_url) && (
                  <Button
                    color="primary"
                    tag="a"
                    href={googleReviewUrl || preview.google_review_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-100"
                  >
                    {t("public_review.actions.google")}
                  </Button>
                )}
              </>
            ) : (
              <>
                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <Label className="form-label">{t("public_review.fields.rating")}</Label>
                <div
                  className="d-flex gap-2 mb-3"
                  role="radiogroup"
                  aria-label={t("public_review.fields.rating")}
                >
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="btn btn-link p-0 border-0 text-decoration-none"
                      style={{
                        fontSize: "2.25rem",
                        lineHeight: 1,
                        color: star <= displayRating ? "#f0b429" : "#d8d2cb",
                      }}
                      aria-label={t("public_review.starLabel", { count: star })}
                      aria-checked={rating === star}
                      role="radio"
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(star)}
                    >
                      ★
                    </button>
                  ))}
                </div>

                <Label className="form-label" htmlFor="review-comment">
                  {t("public_review.fields.comment")}
                </Label>
                <Input
                  id="review-comment"
                  type="textarea"
                  rows={3}
                  maxLength={500}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("public_review.fields.commentPlaceholder")}
                  className="mb-1"
                />
                <p className="text-muted small mb-3">
                  {t("public_review.fields.commentHint", { count: comment.length })}
                </p>

                <Button
                  color="dark"
                  className="w-100"
                  disabled={submitting || rating < 1}
                  onClick={() => void handleSubmit()}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {t("public_review.actions.sending")}
                    </>
                  ) : (
                    t("public_review.actions.submit")
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </Container>
    </div>
  );
};

export default PublicAppointmentReviewPage;
